import {
    OutreachStatusFilter,
    ProcessStatusGroup,
    RecruitingStatusFilter,
    SortIndexField,
} from '__generated__/graphql'

export type StarRatingRange = { gte: number; lte: number } | undefined
export type DateRange = { gte?: string; lte?: string } | undefined

export type CandidateFilterState = {
    colors: string[]
    processStatus: string[]
    processStatusGroup: ProcessStatusGroup
    name: string | undefined
    email: string | undefined
    phone: string | undefined
    recruitingStatus: RecruitingStatusFilter[]
    outreachStatus: OutreachStatusFilter[]
    starRating: StarRatingRange
    screeningNoteTemplateIds: string[]
    screeningNoteStarRating: StarRatingRange
    companyIds: string[]
    companyTimeFrame: string | undefined
    companyDateRange: DateRange
    collegeIds: string[]
}

export type SortState = Array<{ field: SortIndexField; order: 'asc' | 'desc' }>

type FieldCodec<T> = {
    encode: (value: T) => string | null
    decode: (str: string) => T
    default: T
}

const stringArray: FieldCodec<string[]> = {
    encode: (v) => (v.length ? v.map(encodeURIComponent).join('_') : null),
    decode: (s) => s.split('_').filter(Boolean).map(decodeURIComponent),
    default: [],
}

const stringArrayNoEncode: FieldCodec<string[]> = {
    encode: (v) => (v.length ? v.join('_') : null),
    decode: (s) => s.split('_').filter(Boolean),
    default: [],
}

const optionalString: FieldCodec<string | undefined> = {
    encode: (v) => (v ? encodeURIComponent(v) : null),
    decode: (s) => decodeURIComponent(s),
    default: undefined,
}

const optionalStringNoEncode: FieldCodec<string | undefined> = {
    encode: (v) => v || null,
    decode: (s) => s,
    default: undefined,
}

const starRatingCodec: FieldCodec<StarRatingRange> = {
    encode: (v) => (v ? `${v.gte}_${v.lte}` : null),
    decode: (s) => {
        const [gte, lte] = s.split('_').map(parseFloat)
        return !isNaN(gte) && !isNaN(lte) ? { gte, lte } : undefined
    },
    default: undefined,
}

const dateRangeCodec: FieldCodec<DateRange> = {
    encode: (v) => (v?.gte || v?.lte ? `${v.gte || ''}_${v.lte || ''}` : null),
    decode: (s) => {
        const [gte, lte] = s.split('_')
        return { gte: gte || undefined, lte: lte || undefined }
    },
    default: undefined,
}

const processStatusGroupCodec: FieldCodec<ProcessStatusGroup> = {
    encode: (v) => (v !== ProcessStatusGroup.Submitted ? v : null),
    decode: (s) => s as ProcessStatusGroup,
    default: ProcessStatusGroup.Submitted,
}

type FilterCodecEntry<T> = {
    codec: FieldCodec<T>
    urlKey: string
}

const FILTER_CODECS: {
    [K in keyof CandidateFilterState]: FilterCodecEntry<CandidateFilterState[K]>
} = {
    processStatusGroup: {
        codec: processStatusGroupCodec,
        urlKey: 'processStatusGroup',
    },
    name: { codec: optionalString, urlKey: 'name' },
    email: { codec: optionalString, urlKey: 'email' },
    phone: { codec: optionalString, urlKey: 'phone' },
    colors: { codec: stringArray, urlKey: 'colors' },
    processStatus: { codec: stringArray, urlKey: 'processStatus' },
    recruitingStatus: {
        codec: stringArrayNoEncode as FieldCodec<RecruitingStatusFilter[]>,
        urlKey: 'recruitingStatus',
    },
    outreachStatus: {
        codec: stringArrayNoEncode as FieldCodec<OutreachStatusFilter[]>,
        urlKey: 'outreachStatus',
    },
    starRating: { codec: starRatingCodec, urlKey: 'starRating' },
    screeningNoteTemplateIds: {
        codec: stringArrayNoEncode,
        urlKey: 'screeningNoteTemplates',
    },
    screeningNoteStarRating: {
        codec: starRatingCodec,
        urlKey: 'screeningNoteStarRating',
    },
    companyIds: { codec: stringArrayNoEncode, urlKey: 'companyIds' },
    companyTimeFrame: {
        codec: optionalStringNoEncode,
        urlKey: 'companyTimeFrame',
    },
    companyDateRange: { codec: dateRangeCodec, urlKey: 'companyDateRange' },
    collegeIds: { codec: stringArrayNoEncode, urlKey: 'collegeIds' },
}

const URL_KEY_TO_FIELD = Object.fromEntries(
    Object.entries(FILTER_CODECS).map(([field, { urlKey }]) => [urlKey, field])
) as Record<string, keyof CandidateFilterState>

export const DEFAULT_FILTERS: CandidateFilterState = Object.fromEntries(
    Object.entries(FILTER_CODECS).map(([field, { codec }]) => [
        field,
        codec.default,
    ])
) as CandidateFilterState

export function encodeFilters(filters: CandidateFilterState): string {
    const parts: string[] = []
    for (const [field, { codec, urlKey }] of Object.entries(FILTER_CODECS)) {
        const value = filters[field as keyof CandidateFilterState]
        const encoded = (codec as FieldCodec<unknown>).encode(value)
        if (encoded) parts.push(`${urlKey}~${encoded}`)
    }
    return parts.join('.')
}

export function decodeFilters(str: string): CandidateFilterState {
    const result = { ...DEFAULT_FILTERS }
    if (!str) return result

    for (const part of str.split('.')) {
        const tildeIdx = part.indexOf('~')
        if (tildeIdx === -1) continue
        const urlKey = part.slice(0, tildeIdx)
        const value = part.slice(tildeIdx + 1)
        const field = URL_KEY_TO_FIELD[urlKey]
        if (field) {
            const { codec } = FILTER_CODECS[field]
            ;(result as Record<string, unknown>)[field] = codec.decode(value)
        }
    }
    return result
}

export function hasActiveFilters(filters: CandidateFilterState): boolean {
    for (const [field, { codec }] of Object.entries(FILTER_CODECS)) {
        const value = filters[field as keyof CandidateFilterState]
        if ((codec as FieldCodec<unknown>).encode(value) !== null) {
            return true
        }
    }
    return false
}

const SORT_FIELD_TO_KEY: Partial<Record<SortIndexField, string>> = {
    [SortIndexField.FirstName]: 'firstName',
    [SortIndexField.LastName]: 'lastName',
    [SortIndexField.PreferredName]: 'preferredName',
    [SortIndexField.StarRating]: 'starRating',
    [SortIndexField.ProcessStatus]: 'processStatus',
    [SortIndexField.ProcessStatusUpdatedAt]: 'processStatusUpdatedAt',
    [SortIndexField.DateOnRoster]: 'dateOnRoster',
}

const KEY_TO_SORT_FIELD: Record<string, SortIndexField> = Object.fromEntries(
    Object.entries(SORT_FIELD_TO_KEY).map(([field, key]) => [
        key,
        field as SortIndexField,
    ])
)

export function encodeSort(sort: SortState): string {
    return sort
        .map((s) => {
            const key = SORT_FIELD_TO_KEY[s.field]
            return key ? `${key}~${s.order}` : null
        })
        .filter(Boolean)
        .join('.')
}

export function decodeSort(str: string): SortState {
    if (!str) return []

    return str
        .split('.')
        .map((part) => {
            const [key, order] = part.split('~')
            const field = KEY_TO_SORT_FIELD[key]
            if (!field || (order !== 'asc' && order !== 'desc')) return null
            return { field, order }
        })
        .filter((s): s is SortState[number] => s !== null)
}

export const mapSortFieldToString = (field: SortIndexField): string => {
    switch (field) {
        case SortIndexField.FirstName:
            return 'firstName'
        case SortIndexField.LastName:
            return 'lastName'
        case SortIndexField.PreferredName:
            return 'preferredName'
        case SortIndexField.StarRating:
            return 'starRating'
        case SortIndexField.ProcessStatus:
            return 'processStatus'
        case SortIndexField.ProcessStatusUpdatedAt:
            return 'processStatusUpdatedAt'
        case SortIndexField.DateOnRoster:
            return 'dateOnRoster'
        default:
            return 'name'
    }
}

export const getViewFromTab = (
    tab: ProcessStatusGroup
):
    | 'client-portal-submitted'
    | 'client-portal-in-process'
    | 'client-portal-out-of-process' => {
    switch (tab) {
        case ProcessStatusGroup.Submitted:
            return 'client-portal-submitted'
        case ProcessStatusGroup.InProcess:
            return 'client-portal-in-process'
        case ProcessStatusGroup.OutOfProcess:
            return 'client-portal-out-of-process'
        default:
            return 'client-portal-submitted'
    }
}

export const processStatusGroupToLabel = (
    group: ProcessStatusGroup
): string => {
    switch (group) {
        case ProcessStatusGroup.Submitted:
            return 'Submitted'
        case ProcessStatusGroup.InProcess:
            return 'In Process'
        case ProcessStatusGroup.OutOfProcess:
            return 'Out of Process'
        default:
            return 'Submitted'
    }
}
